import { CheckIcon } from "$components/icons";
import { styled, type Theme } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import type { ComponentProps } from "react";
import {
  InvoiceCardSection,
  useInvoiceCardLayout,
} from "../invoice-card-shell";
import type { FormatInvoiceMoney } from "./invoice-money";
import {
  INVOICE_CARD_COLORS,
  INVOICE_CARD_RADIUS,
  invoiceAmountColor,
} from "./tokens";

const MEMBERSHIP_PLACEHOLDER = "—";
const COVERAGE_META_NOTE = "applied on finalise";
const BADGE_SIZE = 30;
const CASHIER_SKELETON_WIDTH = 70;

const SectionTitle = styled("div")<Theme>(({ theme }) => ({
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
  marginBottom: 10,
}));

const PaymentList = styled("div")<Theme>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const PaymentRow = styled("div")<Theme>(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: `${BADGE_SIZE}px 1fr auto`,
  alignItems: "center",
  gap: "2px 10px",
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${INVOICE_CARD_COLORS.border}`,
  borderRadius: INVOICE_CARD_RADIUS.inner,
  padding: "9px 12px",
}));

const Badge = styled("div")<Theme, { "data-kind": InvoicePaymentKind }>(
  ({ theme, ...props }) => ({
    gridColumn: 1,
    gridRow: "1 / 3",
    width: BADGE_SIZE,
    height: BADGE_SIZE,
    borderRadius: INVOICE_CARD_RADIUS.pill,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor:
      props["data-kind"] === "insurer-coverage"
        ? INVOICE_CARD_COLORS.insurerBadge
        : INVOICE_CARD_COLORS.paidBadge,
    color:
      props["data-kind"] === "insurer-coverage"
        ? theme.palette.brand.main
        : INVOICE_CARD_COLORS.paidText,
    "& .MuiSvgIcon-root": { fontSize: 16 },
  }),
);

const PaymentLabel = styled("div")<Theme>(({ theme }) => ({
  gridColumn: 2,
  gridRow: 1,
  fontWeight: 600,
  fontSize: 13.5,
  color: theme.palette.text.primary,
  minWidth: 0,
}));

const PaymentAmount = styled("div")<Theme, { "data-kind": InvoicePaymentKind }>(
  ({ theme, ...props }) => ({
    gridColumn: 3,
    gridRow: 1,
    fontWeight: 700,
    fontSize: 14.5,
    color: invoiceAmountColor(
      theme,
      props["data-kind"] === "insurer-coverage" ? "insurer" : "self-pay",
    ),
    whiteSpace: "nowrap",
    textAlign: "right",
  }),
);

const PaymentMeta = styled("div")<Theme>(({ theme }) => ({
  gridColumn: "2 / 4",
  gridRow: 2,
  fontWeight: 500,
  fontSize: 12,
  color: theme.palette.text.secondary,
}));

const OfflineNote = styled("span")<Theme>(({ theme }) => ({
  fontStyle: "italic",
  color: theme.palette.text.disabled,
}));

export type InvoicePaymentKind = "insurer-coverage" | "patient-payment";

/**
 * The name behind `actionedByUserId` is only reachable online, so a row states
 * why it has no name rather than silently dropping the segment.
 */
export type CashierLabel =
  | { status: "resolved"; name: string }
  | { status: "loading" }
  | { status: "unavailable" };

/**
 * Coverage counts as a payment event: it moves money onto the invoice the
 * moment it is finalised. `recordedAt` only orders the list — `timestampLabel`
 * is what the row shows, so formatting stays with the caller.
 */
export type InvoicePaymentEntry =
  | {
      kind: "insurer-coverage";
      id: string;
      insurerName: string;
      amount: number;
      recordedAt: Date;
      timestampLabel: string;
      membershipNumber?: string;
    }
  | {
      kind: "patient-payment";
      id: string;
      methodLabel: string;
      amount: number;
      recordedAt: Date;
      timestampLabel: string;
      /** Absent offline: the invoice document carries no receipt sequence. */
      receiptNumber?: string;
      reference?: string;
      cashier?: CashierLabel;
    };

/**
 * Coverage always leads, whatever the clock says — a patient payment can only
 * ever settle what insurance left behind.
 */
export const sortInvoicePaymentEntries = (
  entries: InvoicePaymentEntry[],
): InvoicePaymentEntry[] =>
  [...entries].sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === "insurer-coverage" ? -1 : 1;
    return a.recordedAt.getTime() - b.recordedAt.getTime();
  });

const paymentLabel = (entry: InvoicePaymentEntry): string =>
  entry.kind === "insurer-coverage"
    ? `${entry.insurerName} coverage`
    : entry.methodLabel;

const metaSegments = (
  entry: InvoicePaymentEntry,
  isCompact: boolean,
): string[] => {
  if (entry.kind === "insurer-coverage") {
    return isCompact
      ? [entry.timestampLabel]
      : [
          entry.timestampLabel,
          COVERAGE_META_NOTE,
          `membership ${entry.membershipNumber || MEMBERSHIP_PLACEHOLDER}`,
        ];
  }

  return [
    entry.timestampLabel,
    entry.receiptNumber,
    ...(isCompact ? [] : [entry.reference]),
  ].filter((segment): segment is string => Boolean(segment));
};

const cashierOnEntry = (
  entry: InvoicePaymentEntry,
  isCompact: boolean,
): CashierLabel | undefined =>
  entry.kind === "patient-payment" && !isCompact ? entry.cashier : undefined;

const CashierSegment: FC<{ cashier: CashierLabel }> = ({ cashier }) => {
  if (cashier.status === "resolved") return <>by {cashier.name}</>;
  if (cashier.status === "unavailable")
    return <OfflineNote>cashier offline</OfflineNote>;

  return (
    <>
      by{" "}
      <Skeleton
        component="span"
        variant="text"
        width={CASHIER_SKELETON_WIDTH}
        style={{ display: "inline-block", verticalAlign: "middle" }}
        data-testid="invoicing.v2.card.cashierLoading"
      />
    </>
  );
};

/**
 * Receipt printing lives on the footer's print action, so the rows carry no
 * controls.
 */
export const PaymentHistory: FC<
  {
    payments: InvoicePaymentEntry[];
    formatMoney: FormatInvoiceMoney;
  } & Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({ payments, formatMoney, ...restProps }) => {
  const { isCompact } = useInvoiceCardLayout();

  if (payments.length === 0) return null;

  return (
    <InvoiceCardSection
      tone="alt"
      paddingTop={14}
      paddingBottom={16}
      {...restProps}
    >
      <SectionTitle>Payment history</SectionTitle>
      <PaymentList>
        {sortInvoicePaymentEntries(payments).map((entry) => {
          const cashier = cashierOnEntry(entry, isCompact);

          return (
            <PaymentRow key={entry.id}>
              <Badge data-kind={entry.kind}>
                <CheckIcon color="inherit" />
              </Badge>
              <PaymentLabel>{paymentLabel(entry)}</PaymentLabel>
              <PaymentAmount data-kind={entry.kind}>
                {formatMoney(entry.amount)}
              </PaymentAmount>
              <PaymentMeta>
                {metaSegments(entry, isCompact).join(" · ")}
                {cashier && (
                  <>
                    {" · "}
                    <CashierSegment cashier={cashier} />
                  </>
                )}
              </PaymentMeta>
            </PaymentRow>
          );
        })}
      </PaymentList>
    </InvoiceCardSection>
  );
};
